import * as React from "react"
import { useViewport } from "./viewport.tsx"

/** An overlay root's open-state props, whichever Base UI root it is. */
type OpenProps<Details> = {
  open?: boolean | undefined
  defaultOpen?: boolean | undefined
  onOpenChange?: ((open: boolean, details: Details) => void) | undefined
}

/**
 * Whether an overlay is open, whoever holds that: the caller through `open`, or the root
 * itself when the caller leaves it uncontrolled. The `onOpenChange` it returns goes to the root in
 * place of the caller's, which it calls first.
 */
export function useOverlayOpen<Details extends { isCanceled: boolean }>({
  open,
  defaultOpen,
  onOpenChange,
}: OpenProps<Details>) {
  const [own, setOwn] = React.useState(defaultOpen ?? false)
  const changed = (next: boolean, details: Details) => {
    onOpenChange?.(next, details)
    if (!details.isCanceled) setOwn(next)
  }
  return { open: open ?? own, onOpenChange: changed }
}

/**
 * The form an overlay takes, chosen as it opens and held while it is open. On `compact` it
 * is `docked` (a bottom sheet, or an action sheet); on the desk it is anchored or centred, exactly
 * as it always was. A window resized across 1024px while it is open keeps the form it opened in,
 * so nothing is swapped under the Member's hands; the next opening takes the new one. `docks` false
 * keeps it undocked at every width (a Popover's `anchored`, a Confirmation with nothing to type),
 * and `compact` still says which layout it opened on.
 */
export function useOverlayForm<Details extends { isCanceled: boolean }>(
  props: OpenProps<Details>,
  docks = true,
) {
  const { compact } = useViewport()
  const { open, onOpenChange } = useOverlayOpen(props)
  const [held, setHeld] = React.useState(compact)
  if (!open && held !== compact) setHeld(compact)
  const opened = open ? held : compact
  return { open, compact: opened, docked: docks && opened, onOpenChange }
}
